import React from 'react';
import cx from '../cx';
import Inner from '../Inner';

const HEADING_TAGS = ['h2', 'h3', 'h4', 'h5', 'h6'];

/**
 * Legacy `accordion` block, view only.
 *
 * Three archive help pages use it, holding 75 FAQ answers between them. The
 * stored shape is: settings on the block itself (`headline`, `collapsed`,
 * `non_exclusive`, `right_arrows`, `filtering`, `title_size`, `styles`) and
 * the panels under `data.blocks` / `data.blocks_layout`, each an
 * `accordionPanel` with a `title` and its own blocks.
 *
 * Two fields look interchangeable and are not. `headline` is the visible
 * heading above the accordion ("Frequently Asked Questions"); `title` is the
 * editor's short label for the block ("FAQs") and is deliberately NOT
 * rendered.
 *
 * `filtering` is false on all three stored instances, so no filter box is
 * implemented. That is an omission on purpose, not an oversight -- if a
 * filter is ever wanted it is new work, not a silent half-feature.
 *
 * Collapsed panels keep their content in the DOM behind `hidden` rather than
 * being unmounted: the text is the reason these pages exist, and `hidden`
 * removes it from the accessibility tree without removing it from the
 * document.
 */
const AccordionView = ({ data = {}, ...rest }) => {
  const holder = data.data || {};
  const panels = (holder.blocks_layout?.items || [])
    .map((uid) => [uid, holder.blocks?.[uid]])
    .filter(([, panel]) => panel);

  const nonExclusive = data.non_exclusive !== false;
  // `collapsed` is the stored intent "start closed", and is true on every
  // instance. Absent, we still start closed: an accordion that opens
  // everything at once is just a long page with extra chrome.
  const [open, setOpen] = React.useState(() =>
    data.collapsed === false ? panels.map(([uid]) => uid) : [],
  );

  const toggle = (uid) =>
    setOpen((current) => {
      const isOpen = current.includes(uid);
      if (isOpen) return current.filter((id) => id !== uid);
      return nonExclusive ? [...current, uid] : [uid];
    });

  const baseId = React.useId();
  const TitleTag = HEADING_TAGS.includes(data.title_size)
    ? data.title_size
    : 'h3';
  const fontSize = data.styles?.fontSize;
  const fontWeight = data.styles?.fontWeight;

  if (panels.length === 0) return null;

  return (
    <div
      className={cx('archives-accordion', {
        [`archives-accordion--font-${fontSize}`]: fontSize,
        'archives-accordion--right-arrows': data.right_arrows,
      })}
      style={fontWeight ? { '--archives-accordion-weight': fontWeight } : undefined}
    >
      {data.headline ? (
        <h2 className="archives-accordion__headline">{data.headline}</h2>
      ) : null}
      {panels.map(([uid, panel], index) => {
        const isOpen = open.includes(uid);
        const headerId = `${baseId}-h-${index}`;
        const regionId = `${baseId}-r-${index}`;
        return (
          <div
            key={uid}
            className={cx('archives-accordion__panel', {
              'is-open': isOpen,
            })}
          >
            <TitleTag className="archives-accordion__title">
              <button
                type="button"
                id={headerId}
                className="archives-accordion__trigger"
                aria-expanded={isOpen}
                aria-controls={regionId}
                onClick={() => toggle(uid)}
              >
                <span className="archives-accordion__label">{panel.title}</span>
                <span className="archives-accordion__arrow" aria-hidden="true" />
              </button>
            </TitleTag>
            <div
              id={regionId}
              role="region"
              aria-labelledby={headerId}
              className="archives-accordion__body"
              hidden={!isOpen}
            >
              <Inner holder={panel} {...rest} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AccordionView;
